// Copyright (c) Bentley Systems
import * as React from "react";
import type { EtreeContextMenuState } from "../components/EtreeContextMenu";

type UseEtreeContextMenuProps = {
  /** Called when a node is right-clicked — lets the parent sync its selection state. */
  onNodeSelected: (nodeId: string) => void;
};

type UseEtreeContextMenuResult = {
  contextMenu: EtreeContextMenuState;
  contextMenuRef: React.MutableRefObject<HTMLDivElement | null>;
  closeContextMenu: () => void;
  onNodeContextMenu: (nodeId: string, event: React.MouseEvent) => void;
};

/**
 * Manages context menu open/close state, viewport clamping, and keyboard/click dismissal.
 *
 * Extracted from EtreeContent to reduce its size and allow independent testing.
 *
 * @param onNodeSelected - Callback invoked with the right-clicked nodeId so the parent
 *   can update its selection state before the menu opens.
 */
export const useEtreeContextMenu = ({
  onNodeSelected,
}: UseEtreeContextMenuProps): UseEtreeContextMenuResult => {
  const [contextMenu, setContextMenu] = React.useState<EtreeContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    nodeId: null,
  });
  const contextMenuRef = React.useRef<HTMLDivElement | null>(null);

  const closeContextMenu = React.useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, nodeId: null });
  }, []);

  const onNodeContextMenu = React.useCallback(
    (nodeId: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onNodeSelected(nodeId);
      setContextMenu({ visible: true, x: event.clientX, y: event.clientY, nodeId });
    },
    [onNodeSelected]
  );

  // Close on any outside click
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) closeContextMenu();
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu.visible, closeContextMenu]);

  // Clamp menu position to stay within the viewport after render
  React.useLayoutEffect(() => {
    if (!contextMenu.visible) return;

    const menuElement = contextMenuRef.current;
    if (!menuElement) return;

    const viewportPadding = 8;
    const menuRect = menuElement.getBoundingClientRect();
    const maxX = window.innerWidth - menuRect.width - viewportPadding;
    const maxY = window.innerHeight - menuRect.height - viewportPadding;

    const clampedX = Math.min(
      Math.max(contextMenu.x, viewportPadding),
      Math.max(viewportPadding, maxX)
    );
    const clampedY = Math.min(
      Math.max(contextMenu.y, viewportPadding),
      Math.max(viewportPadding, maxY)
    );

    if (clampedX === contextMenu.x && clampedY === contextMenu.y) return;

    setContextMenu((previous) => {
      if (!previous.visible) return previous;
      if (previous.x === clampedX && previous.y === clampedY) return previous;
      return { ...previous, x: clampedX, y: clampedY };
    });
  }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

  return { contextMenu, contextMenuRef, closeContextMenu, onNodeContextMenu };
};
